import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { Box, Flex, Text } from '@chakra-ui/react';
import {
  closeInnerPopup,
  openInnerPopup,
} from '../../../data/providers/popupsProvider';
import {
  testMatchesFilters,
  buildScoreboardLeaderboard,
} from '../../../data/providers/utils/scoreboardFilters';
import { IGroup, ISelectableTag, ITeam, ITest } from '../../../data/types';
import { clearScoreboardSettings } from '../../../data/writers/settings/scoreboardSettings';
import { useEntities } from '../../../hooks/useEntities';
import { useScoreboardData } from '../../../hooks/useScoreboardData';
import { InvalidSettings } from '../../shared/InvalidSettings';
import { LinearLoading } from '../../shared/loading/LinearLoading';
import { Test } from '../test/Test';
import { ScoreboardSettings } from './ScoreboardSettings';

import { ScoreboardThemeProvider } from './context/ScoreboardThemeContext';
import { ScoreboardContainer } from './components/ScoreboardContainer';
import { ScoreboardHeader } from './components/ScoreboardHeader';
import { ScoreboardErrorBoundary } from './components/ScoreboardErrorBoundary';
import { TopScoresBanner } from './components/TopScoresBanner';
import { ScoreboardColumn } from './components/ScoreboardColumn';
import { ScoreboardRankRow } from './components/ScoreboardRankRow';
import {
  ISplitColumnConfig,
  ISplitViewConfig,
  isCurrentSettingsFormat,
} from './types';
import { useRankChanges } from './hooks/useRankChanges';
import { useConfettiCelebration } from './hooks/useConfettiCelebration';
import { useSplitView } from './hooks/useSplitView';
import { useAutoScroll } from './hooks/useAutoScroll';

const getColumnGroupLabel = (
  columnConfig: ISplitColumnConfig | undefined,
  teams: ITeam[],
  groups: IGroup[]
): string => {
  if (!columnConfig) return '';
  // Secondary filter is the group when split by team (or team when split by group)
  if (columnConfig.secondaryFilterIds?.length > 0) {
    const entities = columnConfig.filterType === 'team' ? groups : teams;
    const entity = entities.find(
      (e: ITeam | IGroup): boolean =>
        e.id === columnConfig.secondaryFilterIds[0]
    );
    return entity?.name ?? '';
  }
  return '';
};

interface IProps extends RouteComponentProps {
  onClose?: () => void;
}

const ScoreboardInternal = withRouter((props: IProps): React.ReactElement => {
  const {
    tests,
    settings,
    loading,
    invalidSettings,
    currentMetric,
    currentTestType,
    savedTheme,
    savedBranding,
    savedDisplay,
  } = useScoreboardData();

  const { teams, groups } = useEntities();

  const splitViewConfig = React.useMemo(
    (): ISplitViewConfig | undefined =>
      settings && isCurrentSettingsFormat(settings)
        ? settings.splitView
        : undefined,
    [settings]
  );

  const {
    filterLeftColumn,
    filterRightColumn,
    isEnabled: isSplitEnabled,
  } = useSplitView({ initialConfig: splitViewConfig });

  const data = React.useMemo((): { list: ITest[]; latest: ITest } => {
    const testsMatchingFilters = tests.filter((test: ITest): boolean =>
      testMatchesFilters(test, settings)
    );
    const list = buildScoreboardLeaderboard(
      testsMatchingFilters,
      settings,
      currentMetric?.value
    );
    return { list, latest: list[0] ?? null };
  }, [
    tests,
    currentTestType?.value,
    settings?.filteredTeams,
    settings?.filteredGroups,
    settings?.tags,
    currentMetric,
  ]);

  const splitData = React.useMemo((): {
    left: ITest[];
    right: ITest[];
  } | null => {
    if (!isSplitEnabled) return null;
    const allTests = tests.filter((test: ITest): boolean => !!test);

    // Use per-column tags for queryHash when available
    const leftTags = splitViewConfig?.leftColumn?.tags;
    const leftSettings =
      leftTags?.length > 0 ? { ...settings, tags: leftTags } : settings;

    const rightTags = splitViewConfig?.rightColumn?.tags;
    const rightSettings =
      rightTags?.length > 0 ? { ...settings, tags: rightTags } : settings;

    return {
      left: buildScoreboardLeaderboard(
        filterLeftColumn(allTests),
        leftSettings,
        currentMetric?.value
      ),
      right: buildScoreboardLeaderboard(
        filterRightColumn(allTests),
        rightSettings,
        currentMetric?.value
      ),
    };
  }, [
    isSplitEnabled,
    tests.length,
    filterLeftColumn,
    filterRightColumn,
    settings,
    splitViewConfig,
    currentMetric,
  ]);

  // Build group labels for split-view columns
  const leftGroupLabel = React.useMemo(
    (): string =>
      getColumnGroupLabel(splitViewConfig?.leftColumn, teams, groups),
    [splitViewConfig?.leftColumn, teams, groups]
  );

  const rightGroupLabel = React.useMemo(
    (): string =>
      getColumnGroupLabel(splitViewConfig?.rightColumn, teams, groups),
    [splitViewConfig?.rightColumn, teams, groups]
  );

  // Build single-column title and filter labels
  const singleColumnTitle = React.useMemo((): string => {
    if (!settings) return '';
    const teamId = settings.filteredTeams?.[0];
    if (!teamId) return '';
    const team = teams.find((t: ITeam): boolean => t.id === teamId);
    return team?.name ?? '';
  }, [settings?.filteredTeams, teams]);

  const singleColumnGroupName = React.useMemo((): string => {
    if (!settings) return '';
    const groupId = settings.filteredGroups?.[0];
    if (!groupId) return '';
    const group = groups.find((g: IGroup): boolean => g.id === groupId);
    return group?.name ?? '';
  }, [settings?.filteredGroups, groups]);

  // Track rank changes and fire confetti when someone overtakes #1
  const { rankChanges } = useRankChanges(data.list);
  const leaderAthleteId = data.list[0]?.athlete?.id;
  useConfettiCelebration(rankChanges, leaderAthleteId);

  // Auto-scroll (for non-split view only, split view columns handle their own)
  const autoScroll = savedDisplay?.autoScroll || false;
  const scrollContainerRef = useAutoScroll<HTMLDivElement>({
    enabled: autoScroll && !isSplitEnabled && !loading,
    listLength: data.list.length,
  });

  const goToTest = (test: ITest): void => {
    openInnerPopup(
      <Test
        test={test}
        onClose={closeInnerPopup(props.history)}
        testList={data.list}
        level={2}
      />,
      props.history,
      `test-${test.id}`
    );
  };

  return (
    <ScoreboardThemeProvider
      initialTheme={savedTheme}
      initialBranding={savedBranding}
      initialDisplay={savedDisplay}
    >
      <ScoreboardContainer showSettingsButton={false}>
        {/* Settings + force trace button */}
        <Flex
          position='absolute'
          top='16px'
          right='64px'
          zIndex={10}
          direction='column'
          gap={3}
          align='flex-end'
        >
          <Flex gap={2} align='center'>
            <Box
              as='button'
              px={3}
              py={1}
              fontSize='12px'
              fontWeight={600}
              color='var(--scoreboard-text-primary, #ffffff)'
              bg='rgba(255, 255, 255, 0.1)'
              border='1px solid rgba(255, 255, 255, 0.2)'
              borderRadius='md'
              cursor='pointer'
              transition='all 0.2s ease-in-out'
              _hover={{ bg: 'rgba(255, 255, 255, 0.2)' }}
              onClick={(): void => {
                const popup = window.open(
                  '/scoreboard/force',
                  'forceTrace',
                  'width=1920,height=1080'
                );
                if (!popup || popup.closed) {
                  window.location.href = '/scoreboard/force';
                }
              }}
              aria-label='Open force trace window'
            >
              Force Trace
            </Box>
            <ScoreboardSettings
              settings={settings}
              disabled={loading}
              iconOnly
            />
          </Flex>
        </Flex>

        <ScoreboardHeader
          testTypeLabel={currentTestType?.label}
          metricLabel={currentMetric?.label}
        />

        {/* Column header (above banner) */}
        {!loading &&
          !invalidSettings &&
          !isSplitEnabled &&
          data.list.length > 0 &&
          singleColumnTitle && (
            <Flex
              bg='var(--scoreboard-card-bg, rgba(0, 0, 0, 0.85))'
              borderRadius='lg'
              mb={3}
              py={4}
              px={6}
              align='center'
              justify='space-between'
            >
              <Box>
                <Text
                  as='h2'
                  fontSize='24px'
                  fontWeight={700}
                  color='var(--scoreboard-text-primary, #ffffff)'
                  textTransform='uppercase'
                  letterSpacing='1px'
                >
                  {singleColumnTitle}
                </Text>
                {singleColumnGroupName && (
                  <Text
                    fontSize='16px'
                    fontWeight={600}
                    color='var(--scoreboard-text-secondary, #b0b0b0)'
                    textTransform='uppercase'
                    letterSpacing='0.5px'
                    mt={1}
                  >
                    {singleColumnGroupName}
                  </Text>
                )}
                {settings?.tags?.length > 0 && (
                  <Flex gap={2} mt={2} flexWrap='wrap'>
                    {settings.tags.map(
                      (tag: ISelectableTag): React.ReactElement => (
                        <Flex
                          key={tag.value}
                          align='center'
                          gap={1}
                          bg='rgba(255, 255, 255, 0.15)'
                          borderRadius='full'
                          px={3}
                          py={1}
                        >
                          <Box
                            as='span'
                            fontFamily="'Material Symbols Outlined'"
                            fontSize='14px'
                            color='var(--scoreboard-text-secondary, #b0b0b0)'
                            lineHeight={1}
                          >
                            sell
                          </Box>
                          <Text
                            fontSize='12px'
                            fontWeight={600}
                            color='var(--scoreboard-text-primary, #ffffff)'
                            textTransform='uppercase'
                            letterSpacing='0.5px'
                          >
                            {tag.label}
                          </Text>
                        </Flex>
                      )
                    )}
                  </Flex>
                )}
              </Box>
              <Text
                fontSize='28px'
                fontWeight={800}
                color='var(--scoreboard-text-primary, #ffffff)'
                letterSpacing='2px'
              >
                2026
              </Text>
            </Flex>
          )}

        {/* Split-view column headers (above banner) */}
        {!loading && !invalidSettings && isSplitEnabled && splitData && (
          <Flex gap={4} mb={3}>
            <Flex
              flex={1}
              bg='var(--scoreboard-card-bg, rgba(0, 0, 0, 0.85))'
              borderRadius='lg'
              py={4}
              px={6}
              align='center'
              justify='space-between'
            >
              <Box>
                <Text
                  as='h2'
                  fontSize='24px'
                  fontWeight={700}
                  color='var(--scoreboard-text-primary, #ffffff)'
                  textTransform='uppercase'
                  letterSpacing='1px'
                >
                  {splitViewConfig?.leftColumn?.title || 'Left'}
                </Text>
                {leftGroupLabel && (
                  <Text
                    fontSize='16px'
                    fontWeight={600}
                    color='var(--scoreboard-text-secondary, #b0b0b0)'
                    textTransform='uppercase'
                    letterSpacing='0.5px'
                    mt={1}
                  >
                    {leftGroupLabel}
                  </Text>
                )}
                {(splitViewConfig?.leftColumn?.tags?.length > 0 ||
                  (!splitViewConfig?.leftColumn?.tags?.length &&
                    settings?.tags?.length > 0)) && (
                  <Flex gap={2} mt={2} flexWrap='wrap'>
                    {(
                      splitViewConfig?.leftColumn?.tags ||
                      settings?.tags ||
                      []
                    ).map(
                      (tag: ISelectableTag): React.ReactElement => (
                        <Flex
                          key={tag.value}
                          align='center'
                          gap={1}
                          bg='rgba(255, 255, 255, 0.15)'
                          borderRadius='full'
                          px={3}
                          py={1}
                        >
                          <Box
                            as='span'
                            fontFamily="'Material Symbols Outlined'"
                            fontSize='14px'
                            color='var(--scoreboard-text-secondary, #b0b0b0)'
                            lineHeight={1}
                          >
                            sell
                          </Box>
                          <Text
                            fontSize='12px'
                            fontWeight={600}
                            color='var(--scoreboard-text-primary, #ffffff)'
                            textTransform='uppercase'
                            letterSpacing='0.5px'
                          >
                            {tag.label}
                          </Text>
                        </Flex>
                      )
                    )}
                  </Flex>
                )}
              </Box>
              <Text
                fontSize='28px'
                fontWeight={800}
                color='var(--scoreboard-text-primary, #ffffff)'
                letterSpacing='2px'
              >
                2026
              </Text>
            </Flex>
            <Flex
              flex={1}
              bg='var(--scoreboard-card-bg, rgba(0, 0, 0, 0.85))'
              borderRadius='lg'
              py={4}
              px={6}
              align='center'
              justify='space-between'
            >
              <Box>
                <Text
                  as='h2'
                  fontSize='24px'
                  fontWeight={700}
                  color='var(--scoreboard-text-primary, #ffffff)'
                  textTransform='uppercase'
                  letterSpacing='1px'
                >
                  {splitViewConfig?.rightColumn?.title || 'Right'}
                </Text>
                {rightGroupLabel && (
                  <Text
                    fontSize='16px'
                    fontWeight={600}
                    color='var(--scoreboard-text-secondary, #b0b0b0)'
                    textTransform='uppercase'
                    letterSpacing='0.5px'
                    mt={1}
                  >
                    {rightGroupLabel}
                  </Text>
                )}
                {(splitViewConfig?.rightColumn?.tags?.length > 0 ||
                  (!splitViewConfig?.rightColumn?.tags?.length &&
                    settings?.tags?.length > 0)) && (
                  <Flex gap={2} mt={2} flexWrap='wrap'>
                    {(
                      splitViewConfig?.rightColumn?.tags ||
                      settings?.tags ||
                      []
                    ).map(
                      (tag: ISelectableTag): React.ReactElement => (
                        <Flex
                          key={tag.value}
                          align='center'
                          gap={1}
                          bg='rgba(255, 255, 255, 0.15)'
                          borderRadius='full'
                          px={3}
                          py={1}
                        >
                          <Box
                            as='span'
                            fontFamily="'Material Symbols Outlined'"
                            fontSize='14px'
                            color='var(--scoreboard-text-secondary, #b0b0b0)'
                            lineHeight={1}
                          >
                            sell
                          </Box>
                          <Text
                            fontSize='12px'
                            fontWeight={600}
                            color='var(--scoreboard-text-primary, #ffffff)'
                            textTransform='uppercase'
                            letterSpacing='0.5px'
                          >
                            {tag.label}
                          </Text>
                        </Flex>
                      )
                    )}
                  </Flex>
                )}
              </Box>
              <Text
                fontSize='28px'
                fontWeight={800}
                color='var(--scoreboard-text-primary, #ffffff)'
                letterSpacing='2px'
              >
                2026
              </Text>
            </Flex>
          </Flex>
        )}

        {/* Top Scores Banner */}
        {!loading && !invalidSettings && (
          <>
            {isSplitEnabled &&
              splitData &&
              (splitData.left[0] || splitData.right[0]) && (
                <TopScoresBanner
                  leftTest={splitData.left[0]}
                  rightTest={splitData.right[0]}
                  metric={currentMetric}
                  onTestClick={goToTest}
                  isSplit
                />
              )}
            {!isSplitEnabled && data.list.length > 0 && (
              <TopScoresBanner
                leftTest={data.list[0]}
                metric={currentMetric}
                onTestClick={goToTest}
              />
            )}
          </>
        )}

        {/* Main content */}
        <Flex flex={1} direction='column' overflow='hidden' py={4} minH={0}>
          {invalidSettings ? (
            <InvalidSettings
              message='Invalid Settings'
              attemptFix={clearScoreboardSettings}
            />
          ) : !loading && isSplitEnabled && splitData ? (
            <Flex flex={1} gap={4} overflow='hidden' minH={0}>
              <ScoreboardColumn
                title={splitViewConfig?.leftColumn?.title || 'Left'}
                list={splitData.left}
                autoScroll={autoScroll}
                metric={currentMetric}
                onTestClick={goToTest}
              />
              <ScoreboardColumn
                title={splitViewConfig?.rightColumn?.title || 'Right'}
                list={splitData.right}
                autoScroll={autoScroll}
                metric={currentMetric}
                onTestClick={goToTest}
              />
            </Flex>
          ) : !loading && data.list.length > 0 ? (
            <Box
              ref={scrollContainerRef}
              flex={1}
              minH={0}
              overflowY='auto'
              display='flex'
              flexDirection='column'
              gap={1}
              role='list'
              aria-label={
                data.list.length > 2
                  ? `Leaderboard rankings 2 through ${data.list.length}`
                  : data.list.length === 2
                  ? 'Leaderboard ranking 2'
                  : 'Leaderboard top score'
              }
              css={{
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
              }}
            >
              {data.list.slice(1).map(
                (test: ITest, index: number): React.ReactElement => (
                  <ScoreboardRankRow
                    key={test.id}
                    test={test}
                    rank={index + 2}
                    metric={currentMetric}
                    onTestClick={goToTest}
                  />
                )
              )}
            </Box>
          ) : !loading && data.list.length === 0 ? (
            <Flex flex={1} align='center' justify='center'>
              <Text
                fontSize='18px'
                color='var(--scoreboard-text-secondary, #b0b0b0)'
              >
                No results found
              </Text>
            </Flex>
          ) : (
            <Flex flex={1} align='center' justify='center'>
              <LinearLoading />
            </Flex>
          )}
        </Flex>
      </ScoreboardContainer>
    </ScoreboardThemeProvider>
  );
});

export const Scoreboard = withRouter(
  (props: IProps): React.ReactElement => (
    <ScoreboardErrorBoundary>
      <ScoreboardInternal {...props} />
    </ScoreboardErrorBoundary>
  )
);
